import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, CardHeader, CardBody, Button, Badge } from 'reactstrap';
import { Plus, FileText, DollarSign, Briefcase, Calendar, Menu } from 'react-feather';
import { fetchFormData } from '../../../../Api/ApiMethods';
import ExpenseDataModal from '../../FinancialOperations/Expenses/ExpenseDataModal';
import PropTypes from 'prop-types';

const AccidentFileExpenseList = ({
    canEdit = true,
    customerId,
    callformId,
    callFormState, // Yeni prop eklendi
    formData, // Form data'dan accidentFiles alacağız
    workFileTypes = [], // workFileTypes prop'u eklendi
    initialCustomerContractRate,
    initialIsMiddlemanContractBased,
    initialMiddlemanContractRate,
    initialMiddlemanContractPrice
}) => {
    const [expensesByAccidentFile, setExpensesByAccidentFile] = useState({});
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [modalMode, setModalMode] = useState('add');
    const [selectedAccidentFileId, setSelectedAccidentFileId] = useState(null);
    
    // AccidentFiles'ı formData'dan al
    const accidentFiles = formData?.accidentFiles || [];
    
    // Previous accidentFiles değerini takip etmek için ref kullanıyoruz
    const prevAccidentFilesRef = useRef();

    const expenseApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/expense';

    useEffect(() => {
        // AccidentFiles'ı formData'dan al
        const currentAccidentFiles = formData?.accidentFiles || [];
        
        // AccidentFiles dizisinin içeriğini karşılaştır, referansını değil
        const accidentFilesChanged = !prevAccidentFilesRef.current || 
            !arraysEqual(prevAccidentFilesRef.current, currentAccidentFiles);
        
        if (accidentFilesChanged) {
            prevAccidentFilesRef.current = currentAccidentFiles;
            
            if (currentAccidentFiles && currentAccidentFiles.length > 0) {
                fetchAllExpenses(currentAccidentFiles);
            } else {
                setLoading(false);
            }
        }
    }, [formData?.accidentFiles]); // formData.accidentFiles'a dependency olarak bak

    // İki dizinin eşit olup olmadığını kontrol eden yardımcı fonksiyon
    const arraysEqual = (a, b) => {
        if (!a || !b) return false;
        if (a.length !== b.length) return false;
        return a.every((val, index) => val === b[index]);
    };

    const fetchAllExpenses = async (currentAccidentFiles) => {
        setLoading(true);
        try {
            const expensesMap = {};
            
            // Her accident file için masrafları getir
            for (const accidentFile of currentAccidentFiles) {
                const accidentFileId = accidentFile.id;
                
                try {
                    const apiUrl = `${expenseApiUrl}?accidentFile=${accidentFileId}`;
                    console.log('Fetching expenses for accident file:', accidentFileId, 'from:', apiUrl);

                    const response = await fetchFormData({
                        apiEndpoint: apiUrl,
                        page: 1,
                        pageSize: 100,
                    });

                    let expensesData = [];
                    if (response && Array.isArray(response.items)) {
                        expensesData = response.items;
                    } else if (response && Array.isArray(response.data?.items)) {
                        expensesData = response.data.items;
                    } else if (Array.isArray(response)) {
                        expensesData = response;
                    }

                    // Debug: masraf yapısını kontrol edelim
                    if (expensesData.length > 0) {
                        console.log('Sample expense structure:', expensesData[0]);
                    }

                    expensesMap[accidentFileId] = expensesData;
                    console.log(`Fetched ${expensesData.length} expenses for accident file ${accidentFileId}`);
                } catch (error) {
                    console.error(`Error fetching expenses for accident file ${accidentFileId}:`, error);
                    expensesMap[accidentFileId] = [];
                }
            }

            setExpensesByAccidentFile(expensesMap);
        } catch (error) {
            console.error('Error fetching expenses:', error);
            setExpensesByAccidentFile({});
        } finally {
            setLoading(false);
        }
    };

    const toggleModal = () => {
        setModalOpen(!modalOpen);
        if (modalOpen) {
            setTimeout(() => {
                setSelectedExpense(null);
                setSelectedAccidentFileId(null);
            }, 300);
        }
    };

    const handleAddExpense = (accidentFileId) => {
        setSelectedExpense(null);
        setSelectedAccidentFileId(accidentFileId);
        setModalMode('add');
        setModalOpen(true);
    };

    const handleEditExpense = (expense) => {
        setSelectedExpense(expense);
        setSelectedAccidentFileId(null);
        setModalMode('modify');
        setModalOpen(true);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Ödendi':
                return 'success';
            case 'Ödenmedi':
                return 'danger';
            default:
                return 'primary';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('tr-TR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return '-';
        }
    };

    const refreshExpenses = () => {
        const currentAccidentFiles = formData?.accidentFiles || [];
        if (currentAccidentFiles.length > 0) {
            fetchAllExpenses(currentAccidentFiles);
        }
    };

    // Accident file'ın dosya türünü kontrol ederek Hak Mahrumiyeti olup olmadığını belirle
    const isAccidentFileHasOnlyLorType = (accidentFileId) => {
        if (!formData || !formData.filesToBeWorked) {
            console.log(`No formData or filesToBeWorked for accident file ${accidentFileId}`);
            return false;
        }
        
        // Bu accident file ID'sine sahip dosyaları bul
        const filesForThisAccident = formData.filesToBeWorked.filter(file => 
            file.accidentFileId === accidentFileId
        );
        
        console.log(`Found ${filesForThisAccident.length} files for accident file ${accidentFileId}:`, filesForThisAccident);
        
        if (filesForThisAccident.length === 0) {
            console.log(`No files found for accident file ${accidentFileId}`);
            return false;
        }
        
        // Tüm dosyaların sadece LOR türünde olup olmadığını kontrol et
        const result = filesForThisAccident.every(file => {
            let fileTypeTitles = [];
            
            // API'den gelen veriyi işleme (ExpertForm'daki mantığı kullan)
            if (Array.isArray(file.fileType)) {
                if (file.fileType.length > 0 && typeof file.fileType[0] === 'object' && file.fileType[0].id) {
                    fileTypeTitles = file.fileType.map(ft => ft.title);
                } else {
                    // ID array durumu - workFileTypes kullanarak title'ları bul
                    fileTypeTitles = file.fileType.map(typeId => {
                        const fileTypeObj = workFileTypes.find(wft => wft.id === typeId);
                        return fileTypeObj ? fileTypeObj.title : 'N/A';
                    });
                }
            } else if (file.fileType && typeof file.fileType === 'object' && !Array.isArray(file.fileType) && file.fileType.id) {
                fileTypeTitles = [file.fileType.title];
            } else if (file._fileTypeTitles && Array.isArray(file._fileTypeTitles)) {
                fileTypeTitles = file._fileTypeTitles;
            }
            
            console.log(`File type titles for accident file ${accidentFileId}:`, fileTypeTitles);
            
            // Bu dosyanın tüm türleri LOR mu?
            if (fileTypeTitles.length === 0) return false;
            
            const isAllLor = fileTypeTitles.every(title => {
                const normalizedTitle = title.toLowerCase();
                return normalizedTitle.includes('hak mahrumiyeti') ||
                    normalizedTitle.includes('lor') ||
                    normalizedTitle.includes('mahrumiyeti');
            });
            
            console.log(`All file types are LOR for this file: ${isAllLor}`, fileTypeTitles);
            return isAllLor;
        });
        
        console.log(`Accident file ${accidentFileId} has only LOR types: ${result}`);
        return result;
    };

    if (loading) {
        return <div className="text-center p-4">Masraflar yükleniyor...</div>;
    }

    if (!accidentFiles || accidentFiles.length === 0) {
        return (
            <div className="text-center p-4">
                <p>Kaza dosyası oluşturulmadığı için masraf bilgileri görüntülenemez.</p>
            </div>
        );
    }

    return (
        <div className="accident-file-expense-list">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">Kaza Dosyası Masrafları</h4>
            </div>

            {accidentFiles
                .filter(accidentFile => {
                    const reapplicationStates = ['Yeni Eksper İşlem Bekleniyor', 'Yeni Eksper Rapor Bekleniyor', 'Yeni Eksper Raporu Ödeme Bekleniyor', 'Yeni Sigorta Başvuru Dilekçe Bekleniyor'];
                    const insuranceOnlyState = 'Ekspersiz Yeni Sigorta Başvuru Dilekçe Bekleniyor';

                    // CallFormState'e göre filtreleme yap
                    if (callFormState === 'Eksper İşlem Bekleniyor' || callFormState === 'Tüm Form') {
                        return true; // Tüm dosyaları göster
                    }

                    if (callFormState === insuranceOnlyState) {
                        return accidentFile.isFromInsurance === true;
                    }
                    
                    if (reapplicationStates.includes(callFormState)) {
                        return accidentFile.isReapplicationFile === true;
                    }

                    // Bu accident file sadece LOR türünde dosyalar içeriyorsa gizle
                    // isSentenced === false → LOR dosyası (icraya yönlendirildi)
                    if (accidentFile.isSentenced === false) {
                        return false;
                    }

                    const hasOnlyLorType = isAccidentFileHasOnlyLorType(accidentFile.id);
                    if (hasOnlyLorType) {
                        return false;
                    }

                    return true;
                })
                .map(accidentFile => {
                const allExpenses = expensesByAccidentFile[accidentFile.id] || [];
                const reapplicationStates = ['Yeni Eksper İşlem Bekleniyor', 'Yeni Eksper Rapor Bekleniyor', 'Yeni Eksper Raporu Ödeme Bekleniyor', 'Yeni Sigorta Başvuru Dilekçe Bekleniyor'];
                const insuranceOnlyState = 'Ekspersiz Yeni Sigorta Başvuru Dilekçe Bekleniyor';
                const isInsuranceOnlyWorkflow = callFormState === insuranceOnlyState;
                const isReapplicationWorkflow = reapplicationStates.includes(callFormState);

                let accidentFileCanEdit = canEdit;
                if (isInsuranceOnlyWorkflow) {
                    accidentFileCanEdit = accidentFileCanEdit && accidentFile.isFromInsurance === true;
                } else if (isReapplicationWorkflow) {
                    accidentFileCanEdit = accidentFileCanEdit && accidentFile.isReapplicationFile === true;
                }
                
                return (
                    <div key={accidentFile.id} className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">
                                <Badge color="primary rounded-pill" className="me-2">Dosya No: {accidentFile.fileNumber}</Badge>
                                {accidentFile.isReapplicationFile === true && <Badge color="success rounded-pill" className="me-2">Yeni</Badge>}
                            </h5>
                            {accidentFileCanEdit && (
                                <Button
                                    color="outline-primary"
                                    size="sm"
                                    onClick={() => handleAddExpense(accidentFile.id)}
                                    className="d-flex align-items-center"
                                >
                                    <Plus size={16} className="me-1" />
                                    Masraf Ekle
                                </Button>
                            )}
                        </div>

                        {allExpenses.length === 0 ? (
                            <p className="text-center p-3">
                                Bu kaza dosyası için kayıtlı masraf bulunamadı.
                            </p>
                        ) : (
                            <Row>
                                {allExpenses.map((expense) => (
                                    <Col key={expense.id} md={6} lg={4} className="mb-3 d-flex">
                                        <Card 
                                            className="shadow-sm hoverable w-100 h-100" 
                                            style={{ 
                                                cursor: accidentFileCanEdit ? 'pointer' : 'default', 
                                                fontSize: '0.95rem' 
                                            }} 
                                            onClick={accidentFileCanEdit ? () => handleEditExpense(expense) : undefined}
                                        >
                                            <CardHeader 
                                                className="d-flex justify-content-between align-items-center text-white py-2" 
                                                style={{ backgroundColor: '#59667a' }}
                                            >
                                                <h6 className="mb-2 mt-2" style={{ wordBreak: 'break-word', maxWidth: '60%' }}>
                                                    {expense.expenseType || 'Masraf'}
                                                </h6>
                                                <div>
                                                    <Badge
                                                        color={getStatusColor(expense.paymentStatus)}
                                                        className="px-2 py-1"
                                                        style={{ fontSize: '13px', fontWeight: 'bold', borderRadius: '8px' }}
                                                    >
                                                        {expense.paymentStatus || 'Ödenmedi'}
                                                    </Badge>
                                                </div>
                                            </CardHeader>
                                            <CardBody className="p-3">
                                                <div className="mb-2 d-flex align-items-center">
                                                    <FileText size={18} className="me-2 text-info" />
                                                    <div>
                                                        <strong>Masraf No:</strong> {expense.expenseNumber || '-'}
                                                    </div>
                                                </div>

                                                <div className="mb-2 d-flex align-items-center">
                                                    <DollarSign size={18} className="me-2 text-success" />
                                                    <div>
                                                        <strong>Tutar:</strong> {expense.amount ? `${new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(expense.amount)}` : '-'}
                                                    </div>
                                                </div>

                                                <div className="mb-2 d-flex align-items-center">
                                                    <Briefcase size={18} className="me-2 text-secondary" />
                                                    <div>
                                                        <strong>Banka:</strong> {expense.associatedBank && expense.associatedBank.bankName
                                                            ? `${expense.associatedBank.bankName} - ${expense.associatedBank.branchName || ''}`
                                                            : '-'}
                                                    </div>
                                                </div>

                                                <div className="mb-2 d-flex align-items-center">
                                                    <Calendar size={18} className="me-2 text-warning" />
                                                    <div>
                                                        <strong>Tarih:</strong> {formatDate(expense.date)}
                                                    </div>
                                                </div>

                                                {expense.invoiceNumber && expense.invoiceNumber !== 0 && (
                                                    <div className="mb-2 d-flex align-items-center">
                                                        <FileText size={18} className="me-2 text-secondary" />
                                                        <div>
                                                            <strong>Fatura No:</strong> {expense.invoiceNumber}
                                                        </div>
                                                    </div>
                                                )}

                                                {expense.description && (
                                                    <div className="d-flex align-items-start mt-2">
                                                        <Menu size={18} className="me-2 text-muted mt-1" />
                                                        <div style={{ wordBreak: 'break-word', fontSize: '0.85rem' }} title={expense.description}>
                                                            <strong>Açıklama:</strong> {expense.description.length > 50 ? `${expense.description.substring(0, 50)}...` : expense.description}
                                                        </div>
                                                    </div>
                                                )}
                                            </CardBody>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        )}
                    </div>
                );
            })}

            {/* Modal for adding/editing expense */}
            {canEdit && (
                <ExpenseDataModal
                    apiEndpoint={expenseApiUrl}
                    isOpen={modalOpen}
                    toggle={toggleModal}
                    mode={modalMode}
                    selectedExpense={selectedExpense}
                    refreshData={refreshExpenses}
                    customerId={customerId}
                    autoAssignCustomer={true}
                    accidentFile={selectedAccidentFileId ? [selectedAccidentFileId] : []}
                    callformId={null} // Kaza dosyası masrafları için callformId kullanmıyoruz
                    initialCustomerContractRate={initialCustomerContractRate}
                    initialIsMiddlemanContractBased={initialIsMiddlemanContractBased}
                    initialMiddlemanContractRate={initialMiddlemanContractRate}
                    initialMiddlemanContractPrice={initialMiddlemanContractPrice}
                />
            )}
        </div>
    );
};

AccidentFileExpenseList.propTypes = {
    canEdit: PropTypes.bool,
    customerId: PropTypes.string,
    callformId: PropTypes.string,
    callFormState: PropTypes.string,
    formData: PropTypes.object.isRequired, // formData artık required
    workFileTypes: PropTypes.arrayOf(PropTypes.object), // workFileTypes prop'u eklendi
    initialCustomerContractRate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    initialIsMiddlemanContractBased: PropTypes.bool,
    initialMiddlemanContractRate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    initialMiddlemanContractPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default AccidentFileExpenseList;
